import { compose } from "redux";
import isNil from "lodash/isNil";
import range from "lodash/range";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import React, { PureComponent } from "react";
import MoreVert from "@material-ui/icons/MoreVert";
import { withStyles } from "@material-ui/core/styles";

import {
  Grid,
  MenuItem,
  ButtonBase,
  Typography,
  IconButton,
} from "@material-ui/core";

import {
  openEmployeeCard,
  displayCardSpecificationGapReport,
} from "features/planner/planner.actions";

import { toCard } from "utils/route.util";
import DropdownMenu from "app/components/DropdownMenu";
import { shiftTypeToString } from "features/planner/planner.mappers";
import Marker from "features/planner/components/Card/EmployeeCard/Marker";
import { hideEmployee } from "features/planner/employee/employee.actions";
import CompetencyStatusIcon from "features/planner/components/CompetencyStatusIcon";
import { getSummarizedCardCompetencyStatusFromState } from "features/planner/planner.selectors";
import EmployeeCardWorkScheduleIcon from "features/planner/components/Card/EmployeeCard/EmployeeCardWorkScheduleIcon";
import { visibleWorkScheduleOverrideReasons } from "features/planner/components/Card/EmployeeCard/EmployeeCardWorkScheduleIcon";

const markerIndices = range(1, 5);

const getBackgroundColor = ({ card }) =>
  card ? card.get("qualificationColor") : undefined;

const getForeColor = theme => ({ card }) =>
  card
    ? card.get("qualificationColor")
      ? theme.palette.getContrastText(card.get("qualificationColor"))
      : theme.palette.text.primary
    : theme.palette.text.primary;

class EmployeeCard extends PureComponent {
  openEmployeeCard = (event, subpage) => {
    const { card, dispatchOpenEmployeeCard } = this.props;

    if (!event.ctrlKey) {
      event.stopPropagation();
      event.preventDefault();
    }

    return dispatchOpenEmployeeCard(card, subpage, !event.ctrlKey);
  };

  displayMissingCompetence = event => {
    event.preventDefault();
    event.stopPropagation();

    const {
      cardId,
      groupId,
      dispatchDisplayCardSpecificationGapReport,
    } = this.props;

    dispatchDisplayCardSpecificationGapReport(cardId, groupId);
  };

  navigateToEmployeeCalendar = event => {
    const { card, dispatchOpenEmployeeCard } = this.props;

    event.stopPropagation();
    event.preventDefault();

    dispatchOpenEmployeeCard(card, "calendar");
  };

  render() {
    const {
      card,
      classes,
      competencyStatus,
      displayDropdownMenu,
      dispatchHideEmployeeCard,
    } = this.props;

    const shiftType = card.getIn(["employee", "deployment", "shiftType"]);

    const currentWorkScheduleOverride = card.get("currentWorkScheduleOverride");

    const displayCurrentWorkScheduleOverride =
      !!currentWorkScheduleOverride &&
      visibleWorkScheduleOverrideReasons.includes(
        currentWorkScheduleOverride.get("overrideReason"),
      );

    return (
      <Grid
        container
        wrap="nowrap"
        component={Link}
        to={toCard(card)}
        className={classes.root}
        onClick={this.openEmployeeCard}
      >
        <Grid
          item
          xs={6}
          container
          wrap="nowrap"
          alignItems="center"
          className={classes.employeeNameContainer}
        >
          {displayCurrentWorkScheduleOverride && (
            <Grid
              item
              component={ButtonBase}
              className={classes.iconButton}
              onClick={this.navigateToEmployeeCalendar}
            >
              <EmployeeCardWorkScheduleIcon
                workScheduleOverride={currentWorkScheduleOverride}
              />
            </Grid>
          )}
          {!isNil(competencyStatus) && !displayCurrentWorkScheduleOverride && (
            <Grid
              item
              component={ButtonBase}
              className={classes.iconButton}
              onClick={this.displayMissingCompetence}
            >
              <CompetencyStatusIcon
                displayTooltipText
                status={competencyStatus}
              />
            </Grid>
          )}
          <Grid
            xs
            item
            noWrap
            zeroMinWidth
            component={Typography}
            title={card.get("name")}
            className={classes.employeeNameText}
          >
            {card.get("name")}
          </Grid>
        </Grid>
        <Grid
          item
          xs={6}
          container
          justifyContent="flex-end"
          className={classes.markerContainer}
        >
          <Grid
            xs
            item
            noWrap
            variant="caption"
            color="textPrimary"
            title="Shift type"
            component={Typography}
            className={classes.markerWrapper}
          >
            {!isNil(shiftType) && shiftTypeToString(shiftType)[0]}
          </Grid>
          <Grid
            xs
            item
            noWrap
            variant="caption"
            color="textPrimary"
            component={Typography}
            title="Days since deployment"
            className={classes.markerWrapper}
          >
            {card.getIn(["employee", "deployment", "daysSinceStart"])}
          </Grid>
          <Grid
            xs
            item
            noWrap
            variant="caption"
            color="textPrimary"
            component={Typography}
            title="Days left offshore"
            className={classes.markerWrapper}
          >
            {card.getIn(["employee", "deployment", "daysLeft"])}
          </Grid>
          {markerIndices
            .map(index => card.getIn(["employee", "markers", index.toString()]))
            .map((markerId, index) => (
              <Grid xs item key={index} className={classes.markerWrapper}>
                <Marker markerId={markerId} />
              </Grid>
            ))}
        </Grid>
        {displayDropdownMenu && (
          <Grid item className={classes.dropdown}>
            <DropdownMenu
              Trigger={
                <IconButton title="More" className={classes.dropdownIconButton}>
                  <MoreVert color="primary" fontSize="small" />
                </IconButton>
              }
            >
              <MenuItem
                onClick={() => dispatchHideEmployeeCard(card.get("id"))}
              >
                Hide
              </MenuItem>
            </DropdownMenu>
          </Grid>
        )}
      </Grid>
    );
  }
}

EmployeeCard.defaultProps = {
  displayDropdownMenu: true,
};

const styles = theme => ({
  employeeNameContainer: {
    color: getForeColor(theme),
    background: getBackgroundColor,
    padding: `0 ${theme.spacing(1)}px`,
  },
  iconButton: {
    marginLeft: -theme.spacing(1) / 2,
    marginRight: theme.spacing(1) / 2,
  },
  markerWrapper: {
    display: "flex",
    fontWeight: 700,
    padding: "0 1px",
    lineHeight: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  markerContainer: {
    overflow: "hidden",
  },
  root: {
    position: "relative",
    "&:hover": {
      opacity: 0.8,
    },
    "&:hover $dropdown": {
      display: "initial",
    },
  },
  dropdown: {
    right: 0,
    display: "none",
    position: "absolute",
    background: theme.palette.common.white,
  },
  dropdownIconButton: {
    color: "inherit",
    padding: 1,
  },
  employeeNameText: {
    ...theme.typography.caption,
    lineHeight: "100%",
    color: getForeColor(theme),
    fontWeight: theme.typography.fontWeightMedium,
  },
});

export default compose(
  connect(
    (state, { cardId, groupId }) => ({
      competencyStatus: groupId
        ? getSummarizedCardCompetencyStatusFromState(state, groupId, cardId)
        : null,
    }),
    {
      dispatchHideEmployeeCard: hideEmployee,
      dispatchOpenEmployeeCard: openEmployeeCard,
      dispatchDisplayCardSpecificationGapReport: displayCardSpecificationGapReport,
    },
  ),
  withStyles(styles),
)(EmployeeCard);
